import { ApolloClient, Operation } from "@apollo/client";
import { AmplifyConfig, createApolloClientWithAuth } from "@homebound/auth-components";
import merge from "lodash/merge";

export type Stage = "local" | "dev" | "qa" | "local-prod" | "prod";

export function getStage(windowLocation = window.location): Stage {
  // Guess stage based on the current window.location.hostname
  const hostname = windowLocation.hostname;
  if (hostname.includes("dev-homebound")) {
    return "dev";
  } else if (hostname.includes("qa-homebound")) {
    return "qa";
  } else if (hostname.includes("localhost.insidemaps.homebound.com")) {
    return "local-prod";
  } else if (hostname.includes("homebound")) {
    return "prod";
  } else {
    return "local";
  }
}

export function isContentPreview(windowLocation = window.location): boolean {
  return /.*\.insidemaps\.(dev|qa)-homebound\.com/.test(windowLocation.hostname);
}

export function qaServerEnvironment(windowLocation = window.location) {
  if (VITE_GIT_BRANCH?.startsWith("qa-")) {
    // Support FE branch names the 'main' qa branch, `qa-1234`, as well as `qa-1234@sub-ticket` WIP branches
    const qaCustomBeBranch = VITE_GIT_BRANCH.split("@")[0];
    // Feature branch qa uses `qa-1234.graphql.dev-homebound.com` subdomains within the dev infra
    return { subDomain: `${qaCustomBeBranch}.graphql`, qaCustomBeBranch };
  }
  // Prod qa uses the top-level `graphql.qa-homebound.com` domain, so doesn't need a subdomain
  return { subDomain: "graphql", qaCustomBeBranch: undefined };
}

export function getGraphQLBaseUrl(stage: Stage = getStage()): string {
  const stageToGraphQL: Record<Stage, string> = {
    local: VITE_GRAPHQL_SERVICE ? VITE_GRAPHQL_SERVICE : `http://localhost:${GQL_PORT}`,
    dev: "https://graphql.dev-homebound.com",
    qa: `https://${qaServerEnvironment().subDomain}.qa-homebound.com`,
    "local-prod": "https://graphql.homebound.com",
    prod: "https://graphql.homebound.com",
  };
  return stageToGraphQL[stage];
}

export function createApolloClient(stage: Stage): ApolloClient<unknown> {
  const baseUrl = getGraphQLBaseUrl(stage);
  return createApolloClientWithAuth(`${baseUrl}/graphql`, {
    serviceName: "insidemaps-tool",
    serviceVersion: VITE_GIT_COMMIT ?? "local",
    retryLinkOptions: {
      attempts: {
        retryIf(error: any, operation: Operation) {
          // Don't retry mutations
          if (operation.query.definitions.some((d) => d.kind === "OperationDefinition" && d.operation === "mutation")) {
            return false;
          }
          if (error instanceof TypeError) {
            console.error("Retrying because fetched rejected: " + error.message);
          }
          // For a failed mutation, error will be a ServerError (error.name === 'ServerName'), don't retry
          // For a rejected fetch (likely transient connectivity issue), error will be a TypeError, so retry
          return error instanceof TypeError;
        },
      },
    },
    apolloClientOptions: {
      connectToDevTools: stage !== "prod",
      assumeImmutableResults: true,
      defaultOptions: {
        // Prefer consistency on CRUD tables/forms. Use watchQuery b/c that's what React's hooks use.
        // For nextFetchPolicy, see https://github.com/apollographql/apollo-client/pull/6712
        watchQuery: { fetchPolicy: "network-only", nextFetchPolicy: "cache-first" },
        query: { fetchPolicy: "network-only" },
      },
    },
  });
}

// Include `aws.cognito.signin.user.admin` so that we can access user attributes like their profile picture:
// https://stackoverflow.com/a/53151260/355031
const scope = ["phone", "email", "openid", "aws.cognito.signin.user.admin"];

export function amplifyConfig(stage: Stage): AmplifyConfig {
  function stageConfig() {
    if (stage === "prod" || stage === "local-prod") {
      return {
        Auth: {
          userPoolId: "us-west-2_Sud41U2uX",
          userPoolWebClientId: "6jbhcfnuji5mor48dg2hg37hvg",
          oauth: { domain: "homebound-internal-prod.auth.us-west-2.amazoncognito.com", scope },
          // Use cookie storage so that non-GraphQL calls (i.e. image downloads) still get the auth token
          cookieStorage: { domain: "insidemaps.homebound.com" },
        },
      };
    }

    return {
      Auth: {
        userPoolId: "us-west-2_u4KsZS7DF",
        userPoolWebClientId: "47jne7hh7uao4il33eq1kftr5c",
        oauth: { domain: "homebound-internal-dev.auth.us-west-2.amazoncognito.com", scope },
        cookieStorage: {
          // Use .<stage>-homebound.com so that our cookies get sent to graphql.<stage>-homebound.com, which
          // knows to look for the cookie as well as the usual Authentication header, so that the
          // GraphQL playground gets auth "for free".
          //
          // This domain pattern is too broad for production, i.e. it would leak our auth cookies onto
          // domains like `somechatvendor.homebound.com`, but it's fine for dev creds.
          //
          // We should also not conflict/overlap with other apps like `app.<stage>-homebound.com` because
          // Cognito puts the application-specific user pool id into the cookie names.
          domain: stage === "local" ? "localhost" : `.${stage}-homebound.com`,
          // Don't use secure so that the cookie works on the http://localhost:4000/graphql GraphQL playground.
          secure: false,
        },
      },
    };
  }

  const { origin } = window.location;

  return merge({}, stageConfig(), {
    Auth: {
      region: "us-west-2",
      oauth: {
        scope: ["phone", "email", "openid"],
        redirectSignIn: origin + "/auth/gcallback",
        redirectSignOut: origin + "/",
        responseType: "code" as const,
      },
    },
  });
}
